// @ts-nocheck
import React, { CSSProperties, ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  Button,
  NextReviewField,
  Stepper,
  BackButton
} from '@lmc/ui-kit'
import { Col } from 'antd'
import { colors } from '@lmc/common/constants'
import {
  Resident,
  ConsentFormTemplate,
  CreateConsentFormsJSON,
  ConsentType
} from '@lmc/common/types'
import dayjs from 'dayjs'
import { Form } from 'antd'
import {
  getTimeOptions,
  onSubmit,
  setNextReviewDateByOptions
} from '../../../utils'
import { v4 as uuidv4 } from 'uuid'
import CloseModal from './components/CloseModal'
import { customConsentFormsEmpty, isMatchingFormBody } from './utils'
import SignNowLaterSection from './components/SignNowLaterSection'
import ModalConsentForms from './components/ModalConsentForms'
import ModalSideControlBar from './components/ModalSideControlBar'
import SignLaterModal from './components/SignLaterModal'
import SignNowModal from './components/SignNowModal'

type ConsentFormModalProps = {
  fetchResidentConsentForms: (resident: string) => void
  isModalConsentVisible: boolean
  item: string
  category: string
  resident: Resident
  setIsModalConsentVisible: (e: boolean) => void
  templates: ConsentFormTemplate[]
  sendConsentForms: (forms: CreateConsentFormsJSON) => Promise<any>
  closeMainModal: () => void
  consentType: ConsentType
  testId?: string
}

const customFormTemplate = (uuid: () => void) => ({
  id: uuid?.(),
  title: 'Custom Title',
  body: '<p>Custom Form</p>',
  editedBy: null,
  editedOn: null,
  createdBy: null,
  createdOn: null,
  isCreated: false
})

const breakPointFormConsentModal = 670

const isDesktop = window.outerWidth > breakPointFormConsentModal

const ConsentFormModal = ({
  closeMainModal,
  fetchResidentConsentForms,
  isModalConsentVisible,
  resident,
  sendConsentForms,
  setIsModalConsentVisible,
  templates,
  item,
  category,
  consentType,
  testId
}: ConsentFormModalProps): ReactElement => {
  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.components.consentModal.${key}`, variable)

  const [sideControlIds, setSideControlIds] = useState([])

  const [signNowLater, setSignNowLater] = useState<
    'now' | 'later' | undefined
  >()

  const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false)

  const [activeStep, setActiveStep] = useState<number>(0)

  const [warning, setWarning] = useState<boolean>(false)

  const [timeOptionNextReview, setTimeOptionNextReview] = useState<
    string | undefined
  >()

  const [nextReviewDate, setNextReviewDate] = useState<string | undefined>()

  const [showSignLaterModal, setShowSignLaterModal] = useState<boolean>(false)

  const [customForms, setCustomForms] = useState([])

  const [copyOfTemplates, setCopyOfTemplates] = useState([])

  useEffect(() => {
    setCopyOfTemplates(templates)
  }, [templates])

  useEffect(() => {
    setCustomForms([customFormTemplate(uuidv4.uuidv4)])
  }, [])

  const finalTemplates = copyOfTemplates?.filter((item) =>
    sideControlIds?.map((item) => item.isSelected && item.id).includes(item.id)
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [form] = Form.useForm()

  let editableTemplates = []

  const templatesToSend = finalTemplates?.map((item) => {
    if (
      !editableTemplates.includes(item.id) &&
      !isMatchingFormBody(item, templates)
    ) {
      return {
        consentTemplate: item.id,
        isCustom: true,
        body: item.body,
        title: item.title,
        isRefused: false,
        type: consentType
      }
    }
    return {
      consentTemplate: item.id,
      title: item.title,
      isRefused: false,
      type: consentType
    }
  })

  const customFormsToSend = customForms
    ?.map((item) => {
      if (item.isCreated) {
        return {
          isCustom: true,
          body: item.body,
          title: item.title,
          isRefused: false,
          type: consentType
        }
      }
      return null
    })
    .filter((item) => item !== null)

  const warningNeeded =
    !customForms.some((item) => item.isCreated === true) &&
    sideControlIds?.every((item) => item.isSelected === false)

  const disabledFinishForm =
    !nextReviewDate ||
    warningNeeded ||
    signNowLater === undefined ||
    customConsentFormsEmpty(customFormsToSend, sideControlIds)

  const copyForSideControl = copyOfTemplates?.map((item) => ({
    isSelected: false,
    title: item.title,
    id: item.id
  }))

  useEffect(() => {
    setSideControlIds(copyForSideControl)
  }, [templates])

  useEffect(() => {
    setNextReviewDateByOptions(timeOptionNextReview, setNextReviewDate, form)
  }, [timeOptionNextReview])

  const handleSelected = (id: string) => {
    const updateArray = sideControlIds.map((item) => {
      if (item.id === id) {
        item.isSelected = !item.isSelected
        return item
      }
      return item
    })
    setSideControlIds(updateArray)
  }

  const onSubmitLater = async () => {
    if (signNowLater === 'now') {
      setShowSignatureModal(true)
      // global sign action
    } else {
      try {
        await sendConsentForms({
          resident: resident.id,
          nextReviewDate: dayjs(nextReviewDate, 'DD-MM-YYYY').toISOString(),
          consents: [...templatesToSend, ...customFormsToSend]
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        // expected output: ReferenceError: nonExistentFunction is not defined
        // Note - error messages will vary depending on browser
      }
      setShowSignLaterModal(true)
    }
  }

  const handleCloseModals = () => {
    const cleanCustomConsentForms = customConsentFormsEmpty(
      customFormsToSend,
      sideControlIds
    )
    if (cleanCustomConsentForms || !warningNeeded) {
      setWarning(true)
    }
    if (warning || warningNeeded || !cleanCustomConsentForms) {
      finalCloseModal()
    }
  }

  const finalCloseModal = () => {
    setShowSignLaterModal(false)
    fetchResidentConsentForms(resident?.id)
    closeMainModal()
    setCustomForms([customFormTemplate(uuidv4.uuidv4)])
    setSignNowLater(undefined)
    setWarning(false)
  }

  const onSubmitSign = async ({ formState }) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    const res = await onSubmit({
      resident,
      nextReviewDate,
      consents: [...templatesToSend, ...customFormsToSend],
      formState,
      item,
      category
    })
    if (res) {
      setShowSignatureModal(false)
      finalCloseModal()
    }
  }

  const handleDateChange = (date, dateString) => {
    if (date !== null) {
      setNextReviewDate(dateString)
    } else {
      setTimeOptionNextReview(undefined)
      setNextReviewDate(undefined)
    }
  }

  const handleEditForm = (id: string) => {
    editableTemplates.push(id)
  }

  const handleCancelEditForm = (id: string) => {
    const filteredTemplates = editableTemplates.filter((item) => item !== id)
    editableTemplates = filteredTemplates
  }

  const handleCustomEntry = () => {
    setCustomForms((oldForms) => [
      ...oldForms.map((item, index) => {
        if (index === customForms.length - 1) {
          return {
            ...item,
            isCreated: true
          }
        }
        return item
      }),
      customFormTemplate(uuidv4.uuidv4)
    ])
  }

  const backButton = (
    <BackButton
      onClick={() => handleCloseModals()}
      data-testid={
        testId
          ? `${testId}-consent-form-back-button`
          : 'consent-form-back-button'
      }
    />
  )

  const finishButton = (
    <Button
      variant="primary"
      style={styles.submitButton}
      onClick={onSubmitLater}
      disabled={disabledFinishForm}
      data-testid={
        testId
          ? `${testId}-consent-form-finish-button`
          : 'consent-form-finish-button'
      }
    >
      {translate('finishForm')}
    </Button>
  )

  const firstCol = (
    <div
      data-testid={
        testId
          ? `${testId}-consent-form-first-column`
          : 'consent-form-first-column'
      }
    >
      {isDesktop && backButton}
      <ModalSideControlBar
        isMobile={!isDesktop}
        handleSelected={handleSelected}
        sideControlIds={sideControlIds}
        translate={translate}
        customEntry={handleCustomEntry}
        testId={testId}
      />
      {!isDesktop && (
        <Button
          variant="default"
          style={styles.nextButton as CSSProperties}
          data-testid={
            testId
              ? `${testId}-consent-form-next-button`
              : 'consent-form-next-button'
          }
        >
          {t('general.buttons.next')}
        </Button>
      )}
    </div>
  )

  const secondCol = (
    <div
      data-testid={
        testId
          ? `${testId}-consent-form-second-column`
          : 'consent-form-second-column'
      }
    >
      <ModalConsentForms
        copyOfTemplates={copyOfTemplates}
        handleCancelEditForm={handleCancelEditForm}
        handleEditForm={handleEditForm}
        resident={resident}
        setCopyOfTemplates={setCopyOfTemplates}
        sideControlIds={sideControlIds}
        templates={templates}
        translate={translate}
        customForms={customForms}
        setCustomForms={setCustomForms}
        testId={testId}
      />
      <SignNowLaterSection
        onChange={setSignNowLater}
        translate={translate}
        value={signNowLater}
        testId={testId}
      />
      <br />
      <Form
        form={form}
        data-testid={
          testId ? `${testId}-consent-form-form` : 'consent-form-form'
        }
      >
        <NextReviewField
          isMobile
          localisation={{
            title: translate('nextReview'),
            datePicker: 'DD-MM-YYYY'
          }}
          dateValue={nextReviewDate}
          datePickerOnChange={handleDateChange}
          radioGroupOnChange={setTimeOptionNextReview}
          timeValue={timeOptionNextReview}
          timeOptions={getTimeOptions(translate)}
          data-testid={
            testId
              ? `${testId}-consent-form-next-review-field`
              : 'consent-form-next-review-field'
          }
        />
        {finishButton}
      </Form>
    </div>
  )

  const steps = [firstCol, secondCol]

  return (
    <>
      <Modal
        greyBackground
        isMobile={!isDesktop}
        fullScreen
        onCancel={() => setIsModalConsentVisible(false)}
        setIsModalVisible={setIsModalConsentVisible}
        isModalVisible={isModalConsentVisible}
        testId={testId}
      >
        {!isDesktop ? (
          <>
            <div
              style={styles.backButtonContainer as CSSProperties}
              data-testid={
                testId
                  ? `${testId}-consent-form-mobile-back-container`
                  : 'consent-form-mobile-back-container'
              }
            >
              {backButton}
            </div>
            <div
              style={styles.stepperContainer}
              data-testid={
                testId
                  ? `${testId}-consent-form-stepper-container`
                  : 'consent-form-stepper-container'
              }
            >
              <Stepper
                allActiveSteps
                onStepClick={setActiveStep}
                activeStep={activeStep}
                steps={[
                  {
                    title: translate('createConsent'),
                    content: undefined,
                    route: ''
                  },
                  {
                    title: translate('viewConsent'),
                    content: undefined,
                    route: undefined
                  }
                ]}
                data-testid={
                  testId
                    ? `${testId}-consent-form-stepper`
                    : 'consent-form-stepper'
                }
              />
            </div>
            <div
              style={
                !isDesktop && activeStep !== 0 ? styles.columnsContainer : {}
              }
              data-testid={
                testId
                  ? `${testId}-consent-form-mobile-content`
                  : 'consent-form-mobile-content'
              }
            >
              {steps[activeStep]}
            </div>
          </>
        ) : (
          <div
            data-testid={
              testId
                ? `${testId}-consent-form-desktop-layout`
                : 'consent-form-desktop-layout'
            }
          >
            <Col style={styles.firstCol}>{firstCol}</Col>
            <Col style={styles.secondCol}>{secondCol}</Col>
          </div>
        )}
      </Modal>
      <SignLaterModal
        handleCloseModals={handleCloseModals}
        setShowSignLaterModal={setShowSignLaterModal}
        showSignLaterModal={showSignLaterModal}
        translate={translate}
        testId={testId}
      />
      <SignNowModal
        onSubmitSign={onSubmitSign}
        resident={resident}
        setShowSignatureModal={setShowSignatureModal}
        showSignatureModal={showSignatureModal}
        translate={translate}
        testId={testId}
      />
      <CloseModal
        showCloseModal={warning}
        setShowCloseModal={setWarning}
        onConfirm={handleCloseModals}
        onCancel={() => setWarning(false)}
        testId={testId}
      />
    </>
  )
}

export const styles = {
  firstCol: {
    marginRight: 100,
    minWidth: 300
  },
  secondCol: {
    marginLeft: 320,
    marginTop: 100,
    marginRight: 50,
    paddingBottom: 50
  },
  title: {
    textAlign: 'center',
    fontSize: 17,
    fontWeight: 700
    // opacity: 0.59,
  },
  subTitle: { textAlign: 'center', fontSize: 13, fontWeight: 300 },
  subTitleStrong: {
    fontWeight: 600,
    color: '#E6699C',
    opacity: 1,
    cursor: 'pointer'
  },
  labelTextBox: { marginTop: 50 },
  listSectionHeader: { marginTop: 50, marginBottom: 50 },
  redTitle: { fontWeight: 700, fontSize: 13, color: colors.red },
  submitButton: {
    marginRight: 4,
    justifyContent: 'end',
    display: 'flex'
  },
  signLaterModal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: 100,
    marginBottom: 80
  },
  linkButton: {
    color: colors.pink,
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  laterTitle: {
    fontWeight: 400,
    color: colors.grey,
    fontSize: 18
  },
  laterSubTitle: {
    fontWeight: 400,
    fontSize: 14,
    marginBottom: 10,
    marginTop: 10
  },
  backButton: {
    marginLeft: 25
  },
  stepperContainer: {
    margin: 50
  },
  backButtonContainer: {
    margin: 30,
    marginLeft: 0,
    marginTop: 0
  },
  columnsContainer: {
    marginLeft: 30,
    marginRight: 30
  },
  nextButton: {
    float: 'right'
  }
}

export default ConsentFormModal
